// frontend/src/utils/speechQueueManager.ts

import { authService } from '../services/auth.service';

const API_BASE = (import.meta.env.PROD && (import.meta.env.VITE_API_BASE_URL === 'http://localhost:8080' || !import.meta.env.VITE_API_BASE_URL)) ? '' : (import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080');

// ── Global Registry: Tüm aktif sesler, stream'ler ve yöneticiler ─────────────
const _activeAudioElements = new Set<HTMLAudioElement>();
const _activeAbortControllers = new Set<AbortController>();
const _allSpeechManagers = new Set<SpeechQueueManager>();
let _globalActiveManager: SpeechQueueManager | null = null;
let _watchersInitialized = false;

/**
 * Aktif bir HTMLAudioElement'i global takip listesine ekler.
 * Döndürülen unregister fonksiyonu çağrıldığında listeden çıkarılır.
 */
export function registerGlobalAudio(audio: HTMLAudioElement): () => void {
  _activeAudioElements.add(audio);
  const cleanup = () => {
    _activeAudioElements.delete(audio);
  };
  audio.addEventListener('ended', cleanup, { once: true });
  audio.addEventListener('error', cleanup, { once: true });
  return cleanup;
}

/**
 * Aktif bir fetch / stream AbortController'ı global takip listesine ekler.
 */
export function registerGlobalAbortController(controller: AbortController): () => void {
  _activeAbortControllers.add(controller);
  return () => {
    _activeAbortControllers.delete(controller);
  };
}

/**
 * Uygulama genelindeki TÜM sesleri, stream'leri, kuyrukları ve TTS motorlarını anında durdurur.
 */
export function stopAllAudioAndTTS() {
  // 1. Tüm aktif SpeechQueueManager'ları durdur
  if (_globalActiveManager) {
    try { _globalActiveManager.stop(); } catch {}
    _globalActiveManager = null;
  }
  _allSpeechManagers.forEach(mgr => {
    try { mgr.stop(); } catch {}
  });

  // 2. Tüm aktif HTMLAudioElement örneklerini durdur ve temizle
  _activeAudioElements.forEach(audio => {
    try {
      audio.onplay = null;
      audio.onpause = null;
      audio.onended = null;
      audio.onerror = null;
      audio.pause();
      audio.currentTime = 0;
      audio.src = '';
    } catch {}
  });
  _activeAudioElements.clear();

  // 3. Devam eden tüm fetch stream bağlantılarını iptal et
  _activeAbortControllers.forEach(controller => {
    try { controller.abort(); } catch {}
  });
  _activeAbortControllers.clear();

  // 4. Tarayıcı yerel SpeechSynthesis motorunu iptal et
  if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
    try { window.speechSynthesis.cancel(); } catch {}
  }

  // 5. Uygulama bileşenlerine (CinAliPlayer, useAiMentorChat vb.) anında durdurma sinyali gönder
  if (typeof window !== 'undefined') {
    try {
      window.dispatchEvent(new CustomEvent('app:audio-stopped'));
    } catch {}
  }
}

export function stopGlobalTTS() {
  stopAllAudioAndTTS();
}

/**
 * Sekme/Pencere değişiminde veya sayfa kapanışında sesleri anında kesmek için dinleyicileri başlatır.
 */
export function initGlobalAudioWatchers() {
  if (_watchersInitialized || typeof window === 'undefined' || typeof document === 'undefined') return;
  _watchersInitialized = true;

  // 1. Sekme değiştiğinde veya tarayıcı simge durumuna küçültüldüğünde
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      stopAllAudioAndTTS();
    }
  });

  // 2. Pencere odağını kaybettiğinde (farklı bir pencereye/uygulamaya geçildiğinde)
  window.addEventListener('blur', () => {
    stopAllAudioAndTTS();
  });

  // 3. Sayfadan ayrılma / kapatma
  window.addEventListener('pagehide', () => {
    stopAllAudioAndTTS();
  });
  window.addEventListener('beforeunload', () => {
    stopAllAudioAndTTS();
  });
}

// Otomatik başlat
initGlobalAudioWatchers();

export class SpeechQueueManager {
  private isSpeaking = false;
  private onBoundaryCallback?: () => void;
  private onStartCallback?: () => void;
  private onCompleteCallback?: () => void;
  private rate: number = 1.0;
  private isBrowserFallbackActive = false;
  private currentRequestId = 0;

  private currentAudio: HTMLAudioElement | null = null;
  private mouthAnimationInterval: any = null;

  constructor() {
    _allSpeechManagers.add(this);
  }

  /** Bu manager'ı global aktif olarak kaydet. Önceki varsa durdur. */
  registerAsActive() {
    if (_globalActiveManager && _globalActiveManager !== this) {
      _globalActiveManager.stop();
    }
    _globalActiveManager = this;
  }

  /** Bu manager global aktif ise kaydı sil */
  unregisterAsActive() {
    if (_globalActiveManager === this) {
      _globalActiveManager = null;
    }
    _allSpeechManagers.delete(this);
  }

  isFallbackActive() {
    return this.isBrowserFallbackActive;
  }

  setRate(rate: number) {
    this.rate = rate;
    if (this.currentAudio) {
      this.currentAudio.playbackRate = rate;
    }
  }

  setCallbacks(callbacks: {
    onBoundary?: () => void;
    onStart?: () => void;
    onComplete?: () => void;
  }) {
    this.onBoundaryCallback = callbacks.onBoundary;
    this.onStartCallback = callbacks.onStart;
    this.onCompleteCallback = callbacks.onComplete;
  }

  private ttsCache: Map<string, string> = new Map(); // text -> blobUrl

  clearCache() {
    this.ttsCache.forEach(url => {
      try { URL.revokeObjectURL(url); } catch {}
    });
    this.ttsCache.clear();
  }

  async prefetch(text: string) {
    if (!text || this.ttsCache.has(text) || this.isBrowserFallbackActive) return;
    try {
      const token = await authService.getAccessToken();
      if (!token) return;

      const res = await fetch(`${API_BASE}/api/ai/tts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ text }),
      });

      if (res.ok) {
        const blob = await res.blob();
        const audioUrl = URL.createObjectURL(blob);
        this.ttsCache.set(text, audioUrl);
      }
    } catch (e) {
      console.warn("TTS prefetch failed:", e);
    }
  }

  async speak(
    text: string,
    callbacks?: {
      onBoundary?: () => void;
      onStart?: () => void;
      onComplete?: () => void;
    }
  ) {
    const requestId = ++this.currentRequestId;
    // Bu manager'ı global aktif olarak kaydet — önceki sesi durdurur
    this.registerAsActive();

    // Önceki seslendirmeyi temizle
    this.stop();

    if (callbacks) {
      this.setCallbacks(callbacks);
    }

    this.isSpeaking = true;
    this.isBrowserFallbackActive = false;

    // Tekrar registerAsActive çünkü stop() isSpeaking'i false yaptı
    _globalActiveManager = this;

    // 1. Önce önbellekten kontrol et (Prefetched Audio)
    if (this.ttsCache.has(text)) {
      try {
        const audioUrl = this.ttsCache.get(text)!;
        const audio = new Audio(audioUrl);
        registerGlobalAudio(audio);
        this.currentAudio = audio;
        audio.playbackRate = this.rate;

        audio.onplay = () => {
          if (requestId !== this.currentRequestId) {
            audio.pause();
            return;
          }
          if (this.onStartCallback) {
            this.onStartCallback();
          }
          this.startMouthAnimation();
        };

        audio.onended = () => {
          if (requestId !== this.currentRequestId) return;
          this.stopMouthAnimation();
          const wasCallback = this.onCompleteCallback;
          this.stop();
          if (wasCallback) {
            wasCallback();
          }
        };

        audio.onerror = () => {
          if (requestId !== this.currentRequestId) return;
          console.error("Önbellekteki Google Cloud TTS oynatılamadı, fallback tetikleniyor.");
          this.stop();
          if (this.onCompleteCallback) this.onCompleteCallback();
        };

        await audio.play();
        return;
      } catch (err) {
        console.warn("Önbellekten oynatma başarısız oldu, normal akışa geçiliyor:", err);
      }
    }

    // 2. Önbellekte yoksa sunucudan çek
    try {
      const token = await authService.getAccessToken();
      if (requestId !== this.currentRequestId) return;

      if (token) {
        const res = await fetch(`${API_BASE}/api/ai/tts`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ text }),
        });

        if (requestId !== this.currentRequestId) return;

        if (res.ok && this.isSpeaking) {
          const blob = await res.blob();
          if (requestId !== this.currentRequestId) return;

          const audioUrl = URL.createObjectURL(blob);
          
          // Gelecekte kullanılmak üzere önbelleğe al
          this.ttsCache.set(text, audioUrl);

          const audio = new Audio(audioUrl);
          registerGlobalAudio(audio);
          this.currentAudio = audio;
          audio.playbackRate = this.rate;

          audio.onplay = () => {
            if (requestId !== this.currentRequestId) {
              audio.pause();
              return;
            }
            if (this.onStartCallback) {
              this.onStartCallback();
            }
            this.startMouthAnimation();
          };

          audio.onended = () => {
            if (requestId !== this.currentRequestId) return;
            this.stopMouthAnimation();
            const wasCallback = this.onCompleteCallback;
            this.stop();
            if (wasCallback) {
              wasCallback();
            }
          };

          audio.onerror = () => {
            if (requestId !== this.currentRequestId) return;
            console.error("Google Cloud TTS oynatma hatası.");
            this.stop();
            if (this.onCompleteCallback) this.onCompleteCallback();
          };

          await audio.play();
          return;
        } else {
          console.warn("Backend Google Cloud TTS response error:", res.status);
        }
      }

      this.stop();
      if (this.onCompleteCallback) this.onCompleteCallback();
    } catch (err) {
      if (requestId !== this.currentRequestId) return;
      console.error("Google Cloud TTS isteği başarısız oldu:", err);
      this.stop();
      if (this.onCompleteCallback) this.onCompleteCallback();
    }
  }

  private startMouthAnimation() {
    this.stopMouthAnimation();
    this.mouthAnimationInterval = setInterval(() => {
      if (this.onBoundaryCallback) {
        this.onBoundaryCallback();
      }
    }, 240); // Ortalama Türkçe hece/kelime ritmi (~240ms)
  }

  private stopMouthAnimation() {
    if (this.mouthAnimationInterval) {
      clearInterval(this.mouthAnimationInterval);
      this.mouthAnimationInterval = null;
    }
  }

  pause() {
    if (this.isSpeaking) {
      if (this.currentAudio) {
        this.currentAudio.pause();
        this.stopMouthAnimation();
      }
    }
  }

  resume() {
    if (this.isSpeaking) {
      if (this.currentAudio) {
        this.currentAudio.play().catch(err => console.warn("Audio resume blocked:", err));
        this.startMouthAnimation();
      }
    }
  }

  stop() {
    this.isSpeaking = false;
    this.stopMouthAnimation();

    // Callback'leri temizle — durdurma sonrası yanlış tetiklenmesin
    this.onCompleteCallback = undefined;

    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.onended = null;
      this.currentAudio.onerror = null;
      this.currentAudio.onplay = null;
      this.currentAudio = null;
    }

    this.unregisterAsActive();
  }
}
